// Single source of truth for "can this user see this nav item / page?".
// Used by RoleBasedGuard (to gate rendering) and by the nav (to hide empty
// sections). Keep this in sync — both must apply identical rules.
//
// Org users are gated by PERMISSION CODES ONLY. The backend returns
// user.permissions[] for every org user (custom_role permissions, or the
// LEGACY_ROLE_PERMISSIONS mapping for legacy orgRoles), so `permission` /
// `permissions` props work uniformly — there is no separate "legacy" path.
// The old `allowed={{ org: [...] }}` prop is only consulted as a fallback on
// guards that have no permission annotation yet, and `allowed.user` /
// `allowed.boss` still govern non-org roles (no permission codes exist there).
//
// The backend enforces the same codes on every API call (HasOrgPermission),
// so this layer is UX — hiding what the server would reject anyway.

export default function checkAccess({
  user,
  hasPermission,
  roles,
  allowed,
  slug,
  permission,
  permissions,
}) {
  if (!user) return false;

  const checkSlug = () => {
    if (typeof slug !== 'string' || slug.trim() === '') return true;
    if (!user.orgPlan?.features || !Array.isArray(user.orgPlan.features)) return false;
    return user.orgPlan.features.some((f) => f.slug === slug);
  };

  // Top-level role type (org/user/kiosk/boss) — always enforced.
  if (roles && roles.length > 0) {
    if (!roles.includes(user.role)) return false;
  }

  if (user.role === 'org') {
    const hasAnyOfList = Array.isArray(permissions) && permissions.length > 0;

    // Permission codes are the gate for ALL org users.
    if (permission && !hasPermission(permission)) return false;
    if (hasAnyOfList && !permissions.some((p) => hasPermission(p))) return false;
    if (permission || hasAnyOfList) return checkSlug();

    // No permission annotation on this guard — legacy fallback.
    if (allowed) {
      if (!allowed.org) return false;
      if (allowed.org.includes('all') || allowed.org.includes(user.orgRole)) {
        return checkSlug();
      }
      // Custom-role users may have a stale/unrelated orgRole; an unannotated
      // guard cannot express their access, so don't lock them out here — the
      // backend still enforces real permissions on every request.
      if (user.custom_role_id) return checkSlug();
      return false;
    }
    return checkSlug();
  }

  // Non-org roles (user/boss/kiosk): legacy allowed check.
  if (allowed) {
    const mainRole = user.role;
    let subRole;
    if (mainRole === 'boss') subRole = user.bossRole || 'super_admin';
    else subRole = user.companyRole;

    if (!allowed[mainRole]) return false;
    if (allowed[mainRole].includes('all') || allowed[mainRole].includes(subRole)) {
      return checkSlug();
    }
    return false;
  }

  return checkSlug();
}

// Recursive visibility for nav items: an item is visible only if it passes its
// own access check AND (when it's a parent) has at least one visible descendant.
// This hides grouping items like "Commerce" when all their sub-items are gated.
export function isItemVisible(item, ctx) {
  if (!item) return false;

  // Items that only make sense when the org's access provider supports a
  // feature (e.g. "Card Assignment" for a provider that has no cards).
  // access_capabilities rides on the user payload alongside permissions[] —
  // it is org config, fixed for the session, so there is nothing to look up.
  //
  // An EMPTY object means "unknown" (no provider set, or a cached user object
  // predating this field), not "supports nothing": stay visible in that case.
  // Hiding a page an existing customer uses is worse than showing a dead one.
  const caps = ctx.user?.access_capabilities;
  if (item.capability && caps && Object.keys(caps).length > 0 && !caps[item.capability]) {
    return false;
  }

  // Items for a module the org has not switched on at all, named by a boolean
  // on the user payload (e.g. `wifi_enabled` for Network Access). Unlike
  // `capability` above, a missing field hides the item rather than showing it:
  // these gate whole pages that are meaningless without the integration, and
  // permission codes cannot express it because managers hold them by default.
  // The cost of that choice is that a session cached from before the field
  // shipped loses the item until the next login, which is the right trade for
  // a page that would otherwise be dead for every org that does not use it.
  if (item.userFlag && !ctx.user?.[item.userFlag]) {
    return false;
  }

  // Guests (account_type 'guest') booked from a public page in an invite
  // only org and belong to no team. The server refuses them these routes
  // (accounts/guest_route_guard.py); this only keeps dead links out of the menu.
  if (item.guestHidden && ctx.user?.account_type === 'guest') {
    return false;
  }

  const ownAccess = checkAccess({
    user: ctx.user,
    hasPermission: ctx.hasPermission,
    roles: item.roles,
    allowed: item.allowed,
    slug: item.slug,
    permission: item.permission,
    permissions: item.permissions,
  });
  if (!ownAccess) return false;

  if (Array.isArray(item.children) && item.children.length > 0) {
    return item.children.some((child) => isItemVisible(child, ctx));
  }
  return true;
}
